import React, { useState } from "react";
import Container from "react-bootstrap/Container";
import Card from "react-bootstrap/Card";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import Alert from "react-bootstrap/Alert";
import Table from "react-bootstrap/Table";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import { Shield, Upload, Download, Users, AlertCircle } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE || "https://app.fintogo.ai/api";
const brandBlue = "#1E88E5";

export default function AdminBulkUpload() {
  const [adminPassword, setAdminPassword] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === "application/json") {
      setFile(selectedFile);
      setError(null);
    } else {
      setError("Bitte eine JSON-Datei auswaehlen.");
      setFile(null);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    if (!adminPassword) {
      setError("Admin-Passwort erforderlich.");
      return;
    }
    if (!file) {
      setError("Bitte eine JSON-Datei auswaehlen.");
      return;
    }

    setLoading(true);

    try {
      const fileContent = await file.text();
      const users = JSON.parse(fileContent);

      if (!Array.isArray(users)) {
        throw new Error("Die JSON-Datei muss ein Array von Benutzern enthalten.");
      }

      // Map users to include school_name from skz field
      const usersWithSchool = users.map((user) => ({
        ...user,
        school_name: user.skz || user.school_name,
      }));

      const res = await fetch(`${API_BASE}/admin/bulk-create-users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          admin_password: adminPassword,
          users: usersWithSchool,
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        const errorMap = {
          invalid_admin_password: "Falsches Admin-Passwort.",
          no_users_provided: "Keine Benutzer in der Datei gefunden.",
          server_error: "Serverfehler. Bitte spaeter erneut versuchen.",
        };
        throw new Error(errorMap[data.error] || data.error || "Upload fehlgeschlagen.");
      }

      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    if (!result?.results?.created?.length) return;

    const headers = ["Name", "E-Mail", "Passwort", "Typ", "Klasse"];
    const rows = result.results.created.map((u) => [
      u.full_name,
      u.email,
      u.password,
      u.user_type === "teacher" ? "Lehrer" : "Schueler",
      u.school_class || "",
    ]);

    const csvContent = [
      headers.join(";"),
      ...rows.map((r) => r.map((cell) => `"${cell}"`).join(";")),
    ].join("\n");

    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `benutzer_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="d-flex align-items-center justify-content-center"
      style={{
        minHeight: "100vh",
        width: "100%",
        background: "linear-gradient(to bottom right, #e0f0ff, white)",
        padding: "2rem 0",
      }}
    >
      <Container style={{ maxWidth: 700, padding: "2rem 1rem" }}>
        <Row className="justify-content-center">
          <Col>
            <Card className="shadow border-0">
              <Card.Body className="p-4 p-md-5">
                <div className="d-flex align-items-center mb-4 gap-3">
                  <div
                    className="rounded-circle d-flex justify-content-center align-items-center"
                    style={{ width: 48, height: 48, background: brandBlue }}
                  >
                    <Shield className="text-white" size={24} />
                  </div>
                  <div>
                    <h1 className="h4 mb-1">Benutzer-Massenimport</h1>
                    <p className="mb-0 text-muted">
                      JSON-Datei hochladen um Benutzerkonten zu erstellen.
                    </p>
                  </div>
                </div>

                {error && (
                  <Alert variant="danger" className="mb-4">
                    <AlertCircle size={18} className="me-2" />
                    {error}
                  </Alert>
                )}

                {!result && (
                  <Form onSubmit={handleUpload}>
                    <Form.Group className="mb-3" controlId="adminPassword">
                      <Form.Label>Admin-Passwort</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Admin-Passwort eingeben"
                        value={adminPassword}
                        onChange={(e) => setAdminPassword(e.target.value)}
                      />
                    </Form.Group>

                    <Form.Group className="mb-4" controlId="jsonFile">
                      <Form.Label>JSON-Datei</Form.Label>
                      <div
                        className="border rounded p-4 text-center"
                        style={{ borderStyle: "dashed", cursor: "pointer" }}
                        onClick={() => document.getElementById("fileInput").click()}
                      >
                        <Upload size={32} className="text-muted mb-2" />
                        <p className="mb-1">
                          {file ? file.name : "Klicken um Datei auszuwaehlen"}
                        </p>
                        <small className="text-muted">JSON-Format erforderlich</small>
                      </div>
                      <input
                        id="fileInput"
                        type="file"
                        accept=".json,application/json"
                        onChange={handleFileChange}
                        style={{ display: "none" }}
                      />
                    </Form.Group>

                    <div className="d-grid">
                      <Button
                        type="submit"
                        disabled={loading || !file}
                        style={{ background: brandBlue, borderColor: brandBlue }}
                      >
                        {loading ? (
                          "Wird verarbeitet..."
                        ) : (
                          <>
                            <Users className="me-2" size={18} />
                            Benutzer erstellen
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                )}

                {result && (
                  <div>
                    <Alert variant="success" className="mb-4">
                      <strong>Import abgeschlossen!</strong>
                      <div className="mt-2">
                        <div>Erstellt: {result.summary.created}</div>
                        <div>Uebersprungen: {result.summary.skipped}</div>
                        <div>Fehler: {result.summary.errors}</div>
                      </div>
                    </Alert>

                    {result.results.created.length > 0 && (
                      <>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h5 className="mb-0">Erstellte Benutzer</h5>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={downloadCSV}
                          >
                            <Download size={16} className="me-1" />
                            CSV herunterladen
                          </Button>
                        </div>

                        <div style={{ maxHeight: 300, overflowY: "auto" }}>
                          <Table striped bordered hover size="sm">
                            <thead>
                              <tr>
                                <th>Name</th>
                                <th>E-Mail</th>
                                <th>Passwort</th>
                                <th>Typ</th>
                              </tr>
                            </thead>
                            <tbody>
                              {result.results.created.slice(0, 10).map((u, i) => (
                                <tr key={i}>
                                  <td>{u.full_name}</td>
                                  <td>{u.email}</td>
                                  <td>
                                    <code>{u.password}</code>
                                  </td>
                                  <td>
                                    {u.user_type === "teacher" ? "Lehrer" : "Schueler"}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                          {result.results.created.length > 10 && (
                            <p className="text-muted text-center">
                              ... und {result.results.created.length - 10} weitere.
                              CSV herunterladen fuer alle.
                            </p>
                          )}
                        </div>
                      </>
                    )}

                    {result.results.skipped.length > 0 && (
                      <div className="mt-4">
                        <h6>Uebersprungen ({result.results.skipped.length})</h6>
                        <ul className="small text-muted">
                          {result.results.skipped.slice(0, 5).map((s, i) => (
                            <li key={i}>
                              {s.firstname} {s.lastname}:{" "}
                              {s.reason === "no_email"
                                ? "Keine E-Mail"
                                : s.reason === "email_exists"
                                ? "E-Mail existiert bereits"
                                : s.reason}
                            </li>
                          ))}
                          {result.results.skipped.length > 5 && (
                            <li>... und {result.results.skipped.length - 5} weitere</li>
                          )}
                        </ul>
                      </div>
                    )}

                    <div className="d-grid mt-4">
                      <Button
                        variant="outline-secondary"
                        onClick={() => {
                          setResult(null);
                          setFile(null);
                        }}
                      >
                        Neuen Import starten
                      </Button>
                    </div>
                  </div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
}
